/**
 * Client-side org-audit progress steps for synchronous provider assessments (Ory, Auth0, Entra).
 * Okta async audit uses server-reported steps instead.
 *
 * @module client/utils/orgAuditProgressSteps
 */

/** @typedef {{ id: string, label: string, state?: 'pending' | 'active' | 'done' }} ProgressStep */

export const ORY_AUDIT_STEP_DEFS = [
  { id: "identities", label: "Collecting identities (Kratos)" },
  { id: "oauthClients", label: "Collecting OAuth clients (Hydra)" },
  { id: "schemas", label: "Reading identity schemas" },
  { id: "sessions", label: "Sampling active sessions" },
  { id: "evaluate", label: "Evaluating posture rules" },
];

export const GENERIC_AUDIT_STEP_DEFS = [
  { id: "collect", label: "Collecting tenant data" },
  { id: "evaluate", label: "Evaluating posture rules" },
  { id: "persist", label: "Saving assessment snapshot" },
];

/**
 * @param {Array<{ id: string, label: string }>} defs
 * @param {number} activeIndex
 * @returns {ProgressStep[]}
 */
export function buildProgressSteps(defs, activeIndex) {
  return defs.map((step, i) => ({
    ...step,
    state: i < activeIndex ? "done" : i === activeIndex ? "active" : "pending",
  }));
}

/**
 * Advance steps on an interval until stopped. Returns a cleanup function.
 *
 * @param {(steps: ProgressStep[]) => void} setSteps
 * @param {Array<{ id: string, label: string }>} defs
 * @param {number} [intervalMs]
 */
export function startAssessmentProgressTicker(setSteps, defs, intervalMs = 1400) {
  let activeIndex = 0;
  setSteps(buildProgressSteps(defs, activeIndex));
  const timer = window.setInterval(() => {
    if (activeIndex >= defs.length - 1) return;
    activeIndex += 1;
    setSteps(buildProgressSteps(defs, activeIndex));
  }, intervalMs);
  return () => window.clearInterval(timer);
}

/**
 * @param {(steps: ProgressStep[] | null) => void} setSteps
 * @param {Array<{ id: string, label: string }>} defs
 */
export function finishAssessmentProgress(setSteps, defs) {
  setSteps(defs.map((step) => ({ ...step, state: "done" })));
}
